import { useCallback, useEffect } from "react";
import {
  InternalReportDownloadFormat,
  InternalReportType,
} from "../../../../api/tenantInternalReports";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import { ErrorDisplay } from "../../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../../components/ui/spinners/LoadingSpinner";
import { getCurrentFyKey } from "../../../../lib/utils";
import InternalReportContainer from "../common/InternalReportContainer";
import ReportsPageLayout from "../common/ReportsPageLayout";
import { INTERNAL_REPORT_CONFIG } from "../reportsLanding/internalReportConfig";
import {
  selectFraudByFicCategoryError,
  selectFraudByFicCategorySelectedFy,
  selectFraudByFicCategoryStatus,
} from "../../logic/fraudByFicCategoryReportSlice";
import {
  downloadFraudByFicCategoryReportThunk,
  fetchFraudByFicCategoryReportThunk,
} from "../../logic/fraudByFicCategoryReportThunk";
import FraudByFicCategoryReport from "./FraudByFicCategoryReport";

const reportType = InternalReportType.FRAUD_BY_FIC_CATEGORY;

const FraudByFicCategoryReportPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const status = useAppSelector(selectFraudByFicCategoryStatus);
  const error = useAppSelector(selectFraudByFicCategoryError);
  const selectedFy = useAppSelector(selectFraudByFicCategorySelectedFy);

  const loadFraudByFicCategoryReport = useCallback(
    (fy?: string) => {
      dispatch(fetchFraudByFicCategoryReportThunk({ fy }));
    },
    [dispatch]
  );

  useEffect(() => {
    loadFraudByFicCategoryReport(getCurrentFyKey());
  }, [loadFraudByFicCategoryReport]);

  const handleDownload = async (format: InternalReportDownloadFormat) => {
    await dispatch(
      downloadFraudByFicCategoryReportThunk({
        fy: selectedFy ?? undefined,
        format,
      })
    );
  };

  const hasData = selectedFy !== null;

  return (
    <ReportsPageLayout>
      <InternalReportContainer
        title={INTERNAL_REPORT_CONFIG[reportType].title}
        onDownload={handleDownload}
        downloadEnabled={hasData}
      >
        {status === "failed" ? (
          <ErrorDisplay
            error={error ?? "Failed to load category-wise fraud summary"}
            onRetry={() =>
              loadFraudByFicCategoryReport(selectedFy ?? undefined)
            }
            isRetrying={false}
          />
        ) : !hasData ? (
          <CustomLoadingSpinner
            className="h-full w-full min-h-0 flex-1 m-0"
            title={`Loading ${INTERNAL_REPORT_CONFIG[reportType].title}...`}
          />
        ) : (
          <FraudByFicCategoryReport />
        )}
      </InternalReportContainer>
    </ReportsPageLayout>
  );
};

export default FraudByFicCategoryReportPage;
